/* eslint-disable react/jsx-no-target-blank */
import React from "react";
import cn from "classnames";
import JobCardLogo from "./JobCardLogo";
import styles from "./JobCard.module.scss";

/**
 * Format and display the first value of location with an optional count and descriptor after.
 */
const getLocationDisplay = (locations: string[]) => {
    if (!locations) {
        return "";
    }
    const remainder = locations.length - 1;
    const place = locations?.[0] ?? "";
    const suffix = remainder > 1 ? "s" : "";
    const extras = remainder ? ` + ${remainder} location${suffix}` : "";
    return `${place}${extras}`;
};

interface JobCardProps {
    applyNowHref: string;
    companyHref: string;
    companyName: string;
    jobName: string;
    loading?: boolean;
    locations: string[];
    onApplyNowClick?: () => void;
    onCardClick?: (event: React.SyntheticEvent<HTMLElement>) => void;
    onCompanyNameClick?: () => void;
    onViewJobClick?: () => void;
    onApplyNowRightClick?: () => void;
    onCardRightClick?: (event: React.SyntheticEvent<HTMLElement>) => void;
    onViewJobRightClick?: () => void;
    onCompanyNameRightClick?: () => void;
    postedDate: string;
    viewJobHref: string;
    companyLogo: string;
    showLogo: boolean;
    isLazy?: boolean;
    heading?: "h2" | "h3" | "h4" | "h5" | "h6";
    companyIsLink?: boolean;
}

/**
 * Renders either a link or span with the company name.
 */
const CompanyTitle = (props: {
    companyName: string,
    companyHref: string,
    onCompanyNameClick?: () => void,
    onCompanyNameRightClick?: () => void,
    companyIsLink?: boolean;
}) => {
    const {
        companyName,
        companyHref,
        onCompanyNameClick,
        onCompanyNameRightClick,
        companyIsLink = false,
    } = props;
    return companyIsLink
        ? (
            <a
                className={styles.companyName}
                href={companyHref}
                onClick={onCompanyNameClick}
                onContextMenuCapture={onCompanyNameRightClick}
            >
                {companyName}
            </a>
        )
        : (
            <span className = {styles.companyName}> { companyName }</span >
        );
}

/**
 * Renders a job card with information about a job posting. It takes in various props such as the job name,
 * company name, locations, and links to apply and view the job. The objective of the function is to display
 * this information in a visually appealing and user-friendly way.
 */
const JobCard = (props: JobCardProps) => {
    const {
        applyNowHref,
        companyHref,
        companyName,
        jobName,
        loading,
        locations,
        onApplyNowClick,
        onCardClick,
        onCompanyNameClick,
        onViewJobClick,
        onApplyNowRightClick,
        onCardRightClick,
        onViewJobRightClick,
        onCompanyNameRightClick,
        postedDate,
        viewJobHref,
        companyLogo,
        showLogo,
        isLazy = true,
        heading = "h2",
        companyIsLink = true,
    } = props;

    const jobStyles = onCardClick ? styles.jobCardClickable : styles.jobCard;
    const loadingStyles = loading ? styles.jobCardLoading : null;
    const HeadingTag = heading;

    const onClick = () => {
        if (onApplyNowClick) onApplyNowClick();
        window.open(applyNowHref, "_blank");
    };

    return (
        <div
            className={jobStyles}
            role="presentation"
            onClick={onCardClick}
            onContextMenuCapture={onCardRightClick}
        >
            <div className={styles.contentContainer}>
                <JobCardLogo showLogo={showLogo} url={companyLogo} isLazy={isLazy} />
                <div>
                    <HeadingTag className={styles.jobName}>{jobName}</HeadingTag>
                    <div className={styles.companyLocation}>
                        At
                        {" "}
                        <CompanyTitle {...{
                            companyName,
                            companyHref,
                            onCompanyNameClick,
                            onCompanyNameRightClick,
                            companyIsLink
                        }} />

                        {" - "}
                        {getLocationDisplay(locations)}
                    </div>
                    <div className={styles.postedDate}>
                        Posted on
                        {" "}
                        {postedDate}
                    </div>
                </div>
            </div>
            <div className={styles.linkContainer}>
                <button
                    onClick={onClick}
                    onContextMenuCapture={onApplyNowRightClick}
                    className={cn(styles.applyNowLink, loadingStyles)}
                    aria-label={`Apply to ${jobName} on the Company Site. This opens an external site in a new window.`}
                >
                    Apply on Company Site
                </button>
                <a
                    onClick={onViewJobClick}
                    onContextMenuCapture={onViewJobRightClick}
                    className={cn(styles.viewJobLink, loadingStyles)}
                    href={viewJobHref}
                    target="_blank"
                    aria-label={`View ${jobName} on the Company Site. This opens an external site in a new window.`}
                >
                    View Job
                </a>
            </div>
        </div>
    );
};
JobCard.displayName = "JobCard";

export { JobCard as default };
export type { JobCardProps };
